import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { useSnackbar } from "notistack";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  FileViewer,
  Modal,
  styled,
} from "@monolith-forensics/monolith-ui";
import { CameraIcon, UploadIcon } from "lucide-react";
import { DismissableBanner } from "@/Features/_components/DismissableBanner";
import Loader from "@/components/Loader";
import { useFeatures } from "@/contexts/FeaturesContext";
import SimpleCamera from "@/components/Camera/SimpleCamera";
import { usePermissions } from "@/hooks/usePermissions";
import type { File } from "@/Features/Files/files.types";
import { PhotoGrid } from "@/Features/Files/components/PhotoGrid";
import { mergePhotoEntries } from "@/Features/Files/components/PhotoGrid/mergePhotoEntries";
import { useUploadQueue } from "@/Features/Files/Hooks/useUploadQueue";
import { useLeaveWhileUploadingGuard } from "@/Features/Files/Hooks/useLeaveWhileUploadingGuard";
import { dataUrlsToFiles } from "@/Features/Files/utils/dataUrlsToFiles";
import {
  EvidencePhotosAPI,
  applyUploadedEvidencePhoto,
  evidencePhotosQueryKeys,
  useGetEvidencePhotos,
} from "@/Features/EvidencePhotos";
import type { EvidencePhoto } from "@/Features/EvidencePhotos";
import type { Evidence } from "@/Features/Evidence/evidence.types";
// By path, not the EvidenceFiles barrel, which imports EvidencePhotos back.
import { LearnMoreLink } from "@/Features/EvidenceFiles/ManagedRowIcon";
import { DeleteEvidencePhotoModal } from "./DeleteEvidencePhotoModal";
import { RenameEvidencePhotoModal } from "./RenameEvidencePhotoModal";
import {
  EVIDENCE_PHOTOS_BANNER_ID,
  EVIDENCE_PHOTO_EXTENSIONS,
} from "./evidence-photos.constants";
import { extensionOf, isEvidencePhoto } from "./evidence-photos.utils";

// Minimum gap between refetches triggered by broken <img> links (expired
// signed URLs), so a link that stays broken can't refetch in a loop.
const IMAGE_ERROR_REFETCH_INTERVAL = 30 * 1000;

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  .evidence-photos-banner {
    margin-bottom: 10px;
  }

  .photos-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

// Same transition as the CaseFilesTable drop overlay.
const StyledOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background-color: ${({ theme }) => theme.palette.background.default}cc;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out,
    outline 0.2s ease-in-out;

  border-radius: 2px;
  outline: 2px solid transparent;

  &.dragging-over {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

const preventDefault = (e: React.DragEvent) => e.preventDefault();

const validateExtension = (file: globalThis.File) =>
  isEvidencePhoto(file)
    ? null
    : { code: "unsupported-extension", message: "Unsupported photo type" };

const rejectionMessage = (rejected: FileRejection[]) => {
  const [first, ...others] = rejected;
  const reason = `${
    first.file.name
  }: unsupported photo type (use ${EVIDENCE_PHOTO_EXTENSIONS.join(", ")})`;
  return others.length > 0
    ? `${rejected.length} files were not uploaded. ${reason} (and ${others.length} more)`
    : reason;
};

/**
 * The evidence item's Photos tab on case_docs. Reads, uploads, renames and
 * deletes the item's photos through the ENG-1445 evidence photo routes,
 * whatever the evidence_files_in_case_files flag says. Only image rows show:
 * other files in the item's folder belong to the evidence Files tab.
 */
export const EvidencePhotosContent: React.FC<{ evidenceInfo: Evidence }> = ({
  evidenceInfo,
}) => {
  const { evidence_id, case_id, uuid } = evidenceInfo;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  // The photos only show in Case Files and the Files tab with the flag on.
  const filesInCaseFiles = hasFeatures([FEATURES.EVIDENCE_FILES_IN_CASE_FILES]);
  // Same page, so keep its params (the case it was opened from) and switch
  // the tab the way the tab toggle does.
  const [searchParams] = useSearchParams();
  const filesTabSearch = new URLSearchParams(searchParams);
  filesTabSearch.set("tab", "files");

  // Uploads need the item's case: its folder lives in that case's Files.
  const canUpdate = hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);
  const canUpload = canUpdate && !!case_id;

  const [showCamera, setShowCamera] = useState(false);
  const [viewerFile, setViewerFile] = useState<File | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<File | null>(null);
  const [photoToRename, setPhotoToRename] = useState<File | null>(null);
  const lastImageRefetch = useRef(0);

  const { data, isLoading, isError, error } = useGetEvidencePhotos({
    evidence_id,
  });

  const api = useMemo(
    () => EvidencePhotosAPI.uploaderApi(evidence_id),
    [evidence_id]
  );

  const { uploads, enqueue, dismiss } = useUploadQueue({
    api,
    concurrency: 3,
    hashFile: true,
    onUploaded: (_uploadUuid, result) => {
      const photo = (result as { data?: EvidencePhoto } | undefined)?.data;
      void applyUploadedEvidencePhoto(queryClient, evidence_id, photo);
    },
    // No snackbar: the axios interceptor already toasts HTTP failures, and
    // the failed tile shows the message.
  });

  // Only the item's details page (/evidence/:uuid/:active_tab) keeps this
  // queue mounted; anywhere else, note history included, cancels it.
  const { dialog: leaveDialog } = useLeaveWhileUploadingGuard({
    uploads,
    isSameView: (pathname) => {
      const [, root, itemUuid, tab, ...rest] = pathname.split("/");
      return (
        root === "evidence" && itemUuid === uuid && !!tab && !rest.join("")
      );
    },
  });

  const handleDrop = useCallback(
    (accepted: globalThis.File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        enqueueSnackbar(rejectionMessage(rejected), { variant: "error" });
      }
      if (accepted.length > 0) enqueue(accepted);
    },
    [enqueue, enqueueSnackbar]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept: { "image/*": EVIDENCE_PHOTO_EXTENSIONS.map((ext) => `.${ext}`) },
    validator: validateExtension,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !canUpload,
  });

  const handleCapture = (urls: string[]) => {
    setShowCamera(false);
    dataUrlsToFiles(urls, "evidence-photo")
      .then((files) => enqueue(files))
      .catch(() =>
        enqueueSnackbar("Failed to process photo.", { variant: "error" })
      );
  };

  const handleImageError = useCallback(() => {
    const now = Date.now();
    if (now - lastImageRefetch.current < IMAGE_ERROR_REFETCH_INTERVAL) return;
    lastImageRefetch.current = now;
    queryClient.invalidateQueries({
      queryKey: evidencePhotosQueryKeys.list(evidence_id),
    });
  }, [queryClient, evidence_id]);

  const handleView = (file: File) => {
    setViewerFile(file);
    setShowViewer(true);
  };

  const entries = useMemo(
    () =>
      mergePhotoEntries(
        (data ?? []).filter(isEvidencePhoto),
        Object.values(uploads)
      ),
    [data, uploads]
  );

  const renderBody = () => {
    if (isLoading) return <Loader />;
    if (isError && !data) {
      return (
        <div style={{ marginTop: 20 }}>
          Unable to load photos
          {error instanceof Error && error.message ? `: ${error.message}` : "."}
        </div>
      );
    }
    return (
      <PhotoGrid
        entries={entries}
        onView={handleView}
        onDelete={canUpdate ? setPhotoToDelete : undefined}
        onRename={canUpdate ? setPhotoToRename : undefined}
        onDismiss={dismiss}
        onImageError={handleImageError}
        emptyMessage="No evidence photos"
      />
    );
  };

  return (
    <StyledContainer
      {...getRootProps()}
      {...(!canUpload && {
        // A disabled dropzone drops its drag handlers, so the browser would
        // open a dropped image and navigate away. Make the drop inert.
        onDragOver: preventDefault,
        onDrop: preventDefault,
      })}
    >
      <input {...getInputProps()} />
      <StyledOverlay className={isDragActive ? "dragging-over" : undefined}>
        <div style={{ fontWeight: 600 }}>Drop photos to upload</div>
      </StyledOverlay>
      {filesInCaseFiles && (
        <DismissableBanner
          className="evidence-photos-banner"
          id={EVIDENCE_PHOTOS_BANNER_ID}
          title="Photos you add here also show in this item's Files tab and in Case Files"
          data-testid="evidence-photos-banner"
          actions={
            <>
              <Link
                to={{ search: `?${filesTabSearch}` }}
                replace
                data-testid="evidence-photos-banner-files-tab-link"
              >
                See in Files tab
              </Link>
              <LearnMoreLink />
            </>
          }
        >
          Changes made here show up in those places too.
        </DismissableBanner>
      )}
      {canUpload && (
        <div className="photos-header">
          <Button
            size="xs"
            variant="contained"
            color="primary"
            onClick={open}
            leftSection={<UploadIcon size={14} />}
          >
            Upload Photos
          </Button>
          <Button
            size="xs"
            variant="contained"
            color="primary"
            leftSection={<CameraIcon size={14} />}
            onClick={() => setShowCamera(true)}
          >
            Take Photos
          </Button>
        </div>
      )}
      {renderBody()}
      {leaveDialog}
      {showCamera && (
        <Modal open={showCamera} onClose={() => setShowCamera(false)}>
          <Modal.Title>Take Photos</Modal.Title>
          <div style={{ width: "50vw" }}>
            <SimpleCamera
              onCapture={handleCapture}
              onClose={() => setShowCamera(false)}
            />
          </div>
        </Modal>
      )}
      {photoToDelete && (
        <DeleteEvidencePhotoModal
          show={!!photoToDelete}
          evidenceId={evidence_id}
          file={photoToDelete}
          onClose={() => setPhotoToDelete(null)}
          onSubmit={() => setPhotoToDelete(null)}
        />
      )}
      {photoToRename && (
        <RenameEvidencePhotoModal
          show={!!photoToRename}
          evidenceId={evidence_id}
          file={photoToRename}
          onClose={() => setPhotoToRename(null)}
          onSubmit={() => setPhotoToRename(null)}
        />
      )}
      <FileViewer
        file={{
          name: viewerFile?.name ?? "",
          url: viewerFile?.link,
          ext: extensionOf(viewerFile?.name ?? ""),
          size: viewerFile?.size,
          type: extensionOf(viewerFile?.name ?? ""),
        }}
        open={showViewer}
        onClose={() => setShowViewer(false)}
      />
    </StyledContainer>
  );
};

export default EvidencePhotosContent;
